// Un confrère demande à récupérer son compte : je détiens une de ses parts.
//
// **C'est moi qui l'authentifie, pas le serveur.** Le serveur ne voit qu'une
// adresse e-mail et une clé publique — un imposteur qui connaît l'adresse de mon
// confrère produirait exactement la même demande. La seule chose qui distingue
// l'un de l'autre, c'est un coup de téléphone.
//
// L'interface ne cherche donc pas à rassurer : elle affiche l'empreinte, exige
// qu'on confirme l'avoir vérifiée, et le dit sans détour.

import { useCallback, useEffect, useState } from 'react';
import {
  listPendingRequests,
  releaseShare,
  type PendingRequest,
} from '../crypto/recovery-flow';
import { useAuth } from './store';

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function RecoveryRequests() {
  const session = useAuth((s) => s.session);
  const masterKey = useAuth((s) => s.masterKey);

  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [verified, setVerified] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!session) return;
    try {
      setRequests(await listPendingRequests(session.sessionToken));
    } catch (e) {
      setError(message(e));
    }
  }, [session]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 8000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!session || !masterKey) return null;

  const aTraiter = requests.filter((r) => !r.alreadyReleased);
  if (aTraiter.length === 0) return null;

  async function liberer(request: PendingRequest) {
    setBusy(request.shareId);
    setError(null);
    try {
      await releaseShare(session!.sessionToken, masterKey!.masterKey, request);
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      className="mt-6 rounded border border-red-200 bg-red-50 p-4 text-left"
      data-testid="recovery-requests"
    >
      <h3 className="text-sm font-semibold text-red-900">
        Un confrère demande à récupérer son compte
      </h3>
      <p className="mt-1 text-xs text-red-800">
        Libérer ta part lui rendra l&apos;accès à <strong>tout son cabinet</strong>.
        Le serveur est incapable de vérifier qui demande : c&apos;est{' '}
        <strong>à toi</strong> de l&apos;appeler.
      </p>

      <ul className="mt-4 space-y-4">
        {aTraiter.map((r) => (
          <li key={r.shareId} className="rounded border border-red-200 bg-white p-3">
            <p className="text-sm font-medium text-slate-800">{r.ownerEmail}</p>
            <p className="mt-1 text-[11px] text-slate-500">
              Demandée le {new Date(r.createdAt).toLocaleString('fr-FR')}
            </p>

            <p className="mt-3 text-xs font-medium text-slate-700">
              Empreinte à comparer avec celle qu&apos;il lit sur son écran :
            </p>
            <p
              data-testid="request-fingerprint"
              className="mt-1 select-all break-all font-mono text-sm text-slate-900"
            >
              {r.requesterFingerprint}
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              Si elle diffère, <strong>n&apos;approuve pas</strong> : ta part serait
              scellée à la clé de quelqu&apos;un d&apos;autre.
            </p>

            <label className="mt-3 flex items-start gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                data-testid="confirm-out-of-band"
                checked={verified[r.shareId] ?? false}
                onChange={(e) =>
                  setVerified((v) => ({ ...v, [r.shareId]: e.target.checked }))
                }
              />
              <span>
                Je l&apos;ai joint <strong>par un autre canal</strong> (téléphone,
                de visu) et l&apos;empreinte concorde.
              </span>
            </label>

            <button
              type="button"
              onClick={() => liberer(r)}
              disabled={busy === r.shareId || !verified[r.shareId]}
              data-testid="release-share"
              className="mt-3 w-full rounded bg-red-800 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {busy === r.shareId ? 'Descellement et re-scellement…' : 'Libérer ma part'}
            </button>
          </li>
        ))}
      </ul>

      {error && (
        <p data-testid="recovery-requests-error" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}
